/**
 * MenuUI.ts — Main menu DOM management
 *
 * Creates and destroys menu elements dynamically over the Phaser canvas.
 */

import { BotDifficulty } from '../core/bots';
import { ReplayData, importReplayCode, getRecentReplays, deleteLocalReplay } from '../core/replay';
import { ObstacleDensity } from '../core/obstacles';
import targetIcon from 'lucide-static/icons/target.svg?raw';
import playIcon from 'lucide-static/icons/play.svg?raw';
import helpCircleIcon from 'lucide-static/icons/help-circle.svg?raw';
import awardIcon from 'lucide-static/icons/award.svg?raw';
import usersIcon from 'lucide-static/icons/users.svg?raw';

export interface TeamMode {
  team1Count: number;
  team2Count: number;
  keepTraces: boolean;
  obstacleDensity: ObstacleDensity;
  showCoordinates: boolean;
  turnTimer: number; // 0 for off, else seconds
  chatEquations: boolean;
}

export interface MenuCallbacks {
  onPractice: (difficulty: BotDifficulty, mode: TeamMode) => void;
  onWatchReplay: (data: ReplayData) => void;
  onMultiplayer: () => void;
  onCredits: () => void;
}

export class MenuUI {
  private container: HTMLDivElement | null = null;
  private creditsPanel: HTMLDivElement | null = null;
  private difficultySection: HTMLDivElement | null = null;
  private replayModal: HTMLDivElement | null = null;
  private selectedDifficulty: BotDifficulty = BotDifficulty.Medium;
  private selectedMode: TeamMode = { 
    team1Count: 2, 
    team2Count: 2, 
    keepTraces: true, 
    obstacleDensity: 'none',
    showCoordinates: false,
    turnTimer: 0,
    chatEquations: true,
  };

  /**
   * Creates and displays the main menu.
   */
  show(callbacks: MenuCallbacks): void {
    this.destroy();

    const container = document.createElement('div');
    container.className = 'menu-container';
    container.id = 'main-menu';

    container.innerHTML = `
      <h1 class="menu-title">Mathillery</h1>
      <svg width="200" height="18" viewBox="0 0 200 18" style="margin-top: -12px; margin-bottom: 8px;">
        <path d="M0 9 Q 25 -3 50 9 T 100 9 T 150 9 T 200 9"
              stroke="var(--color-accent)" stroke-width="3" fill="none" stroke-linecap="round"/>
      </svg>
      <p class="menu-subtitle">ARTILLERY <span style="color: var(--color-team1)">∩</span> MATHEMATICS</p>

      <div class="menu-buttons">
        <button class="menu-btn menu-btn--primary" id="btn-practice">
          <span class="btn-icon">${targetIcon}</span>
          Single Player
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-multiplayer">
          <span class="btn-icon">${usersIcon}</span>
          Multiplayer
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-replay">
          <span class="btn-icon">${playIcon}</span>
          Watch Replay
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-tutorial">
          <span class="btn-icon">${helpCircleIcon}</span>
          How to Play
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-credits">
          <span class="btn-icon">${awardIcon}</span>
          Credits
        </button>
      </div>
    `;

    document.body.appendChild(container);
    this.container = container;

    // Event listeners
    const practiceBtn = container.querySelector('#btn-practice') as HTMLButtonElement;
    const replayBtn = container.querySelector('#btn-replay') as HTMLButtonElement;
    const tutorialBtn = container.querySelector('#btn-tutorial') as HTMLButtonElement;
    const multiBtn = container.querySelector('#btn-multiplayer') as HTMLButtonElement;
    const creditsBtn = container.querySelector('#btn-credits') as HTMLButtonElement;

    practiceBtn.addEventListener('click', () => {
      this.showDifficultyPicker(callbacks);
    });

    replayBtn.addEventListener('click', () => {
      this.showReplayModal(callbacks);
    });

    tutorialBtn.addEventListener('click', () => {
      this.showTutorial();
    });

    multiBtn.addEventListener('click', () => {
      callbacks.onMultiplayer();
    });

    creditsBtn.addEventListener('click', () => {
      this.showCredits();
      callbacks.onCredits();
    });

    // Auto-open tutorial on first visit
    if (!localStorage.getItem('mathillery_visited')) {
      localStorage.setItem('mathillery_visited', '1');
      this.showTutorial();
    }
  }

  /**
   * Shows dedicated Practice setup modal card.
   */
  private showDifficultyPicker(callbacks: MenuCallbacks): void {
    if (this.difficultySection) return;

    const modal = document.createElement('div');
    modal.className = 'setup-modal-backdrop';
    modal.innerHTML = `
      <div class="setup-modal-card">
        <div class="setup-modal-header">
          <h2>Match Setup</h2>
          <button class="setup-modal-close" id="btn-close-setup">✕</button>
        </div>

        <div class="setup-grid">
          <div class="setup-option-group">
          <label class="setup-label">Format</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn" data-t1="1" data-t2="1">1v1</button>
            <button class="setup-pill-btn setup-pill-btn--selected" data-t1="2" data-t2="2">2v2</button>
            <button class="setup-pill-btn" data-t1="1" data-t2="3">1v3</button>
            <button class="setup-pill-btn" data-t1="3" data-t2="3">3v3</button>
          </div>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Bot Difficulty</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn setup-pill-btn--selected" data-diff="medium">Medium</button>
            <button class="setup-pill-btn" data-diff="high">High</button>
            <button class="setup-pill-btn" data-diff="impossible">Impossible</button>
            <button class="setup-pill-btn" data-diff="oneshot">1-Shot</button>
          </div>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Trajectory Traces</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn setup-pill-btn--selected" data-traces="true">Visible</button>
            <button class="setup-pill-btn" data-traces="false">Hidden</button>
          </div>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Obstacles</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn setup-pill-btn--selected" data-obs="none">Off</button>
            <button class="setup-pill-btn" data-obs="low">Low</button>
            <button class="setup-pill-btn" data-obs="medium">Med</button>
            <button class="setup-pill-btn" data-obs="high">High</button>
          </div>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Coordinates</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn" data-coords="true">Show</button>
            <button class="setup-pill-btn setup-pill-btn--selected" data-coords="false">Hide</button>
          </div>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Turn Timer</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn setup-pill-btn--selected" data-timer="0">Off</button>
            <button class="setup-pill-btn" data-timer="30">30s</button>
            <button class="setup-pill-btn" data-timer="60">1m</button>
            <button class="setup-pill-btn" data-timer="120">2m</button>
          </div>
        </div>
        
        <div class="setup-option-group">
          <label class="setup-label">Equation Bubbles</label>
          <div class="setup-pill-row">
            <button class="setup-pill-btn setup-pill-btn--selected" data-bubbles="true">On</button>
            <button class="setup-pill-btn" data-bubbles="false">Off</button>
          </div>
        </div>
        </div> <!-- End of setup-grid -->

        <button class="menu-btn menu-btn--primary setup-start-btn" id="btn-start-practice" style="margin-top: 16px;">
          Start Match
        </button>
      </div>
    `;

    document.body.appendChild(modal);
    this.difficultySection = modal;

    const closeBtn = modal.querySelector('#btn-close-setup') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.hideDifficultyPicker());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.hideDifficultyPicker();
    });

    // Mode button listeners
    const modeBtns = modal.querySelectorAll('.setup-pill-btn[data-t1]');
    modeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        modeBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        const el = btn as HTMLElement;
        this.selectedMode.team1Count = parseInt(el.dataset.t1 || '2', 10);
        this.selectedMode.team2Count = parseInt(el.dataset.t2 || '2', 10);
      });
    });

    // Traces button listeners
    const tracesBtns = modal.querySelectorAll('.setup-pill-btn[data-traces]');
    tracesBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        tracesBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        this.selectedMode.keepTraces = (btn as HTMLElement).dataset.traces === 'true';
      });
    });

    // Obstacle button listeners
    const obsBtns = modal.querySelectorAll('.setup-pill-btn[data-obs]');
    obsBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        obsBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        this.selectedMode.obstacleDensity = ((btn as HTMLElement).dataset.obs as ObstacleDensity) || 'none';
      });
    });

    // Coordinates button listeners
    const coordsBtns = modal.querySelectorAll('.setup-pill-btn[data-coords]');
    coordsBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        coordsBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        this.selectedMode.showCoordinates = (btn as HTMLElement).dataset.coords === 'true';
      });
    });

    // Timer button listeners
    const timerBtns = modal.querySelectorAll('.setup-pill-btn[data-timer]');
    timerBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        timerBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        this.selectedMode.turnTimer = parseInt((btn as HTMLElement).dataset.timer || '60', 10);
      });
    });

    // Bubbles button listeners
    const bubblesBtns = modal.querySelectorAll('.setup-pill-btn[data-bubbles]');
    bubblesBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        bubblesBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        this.selectedMode.chatEquations = (btn as HTMLElement).dataset.bubbles === 'true';
      });
    });

    // Difficulty button listeners
    const diffBtns = modal.querySelectorAll('.setup-pill-btn[data-diff]');
    diffBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        diffBtns.forEach(b => b.classList.remove('setup-pill-btn--selected'));
        btn.classList.add('setup-pill-btn--selected');
        const diff = (btn as HTMLElement).dataset.diff;
        if (diff === 'oneshot') this.selectedDifficulty = BotDifficulty.OneShot;
        else if (diff === 'impossible') this.selectedDifficulty = BotDifficulty.Impossible;
        else if (diff === 'high') this.selectedDifficulty = BotDifficulty.High;
        else this.selectedDifficulty = BotDifficulty.Medium;
      });
    });

    // Start button
    const startBtn = modal.querySelector('#btn-start-practice') as HTMLButtonElement;
    startBtn.addEventListener('click', () => {
      this.hideDifficultyPicker();
      callbacks.onPractice(this.selectedDifficulty, this.selectedMode);
    });
  }

  private hideDifficultyPicker(): void {
    if (this.difficultySection) {
      this.difficultySection.remove();
      this.difficultySection = null;
    }
  }

  /**
   * Shows a credits modal.
   */
  private showCredits(): void {
    if (this.creditsPanel) return;

    const panel = document.createElement('div');
    panel.className = 'credits-panel';
    panel.innerHTML = `
      <div class="credits-card">
        <h2>Credits</h2>
        <p>
          <strong>Mathillery</strong> — An artillery game where projectiles
          follow mathematical functions.
        </p>
        <p>
          Inspired by <strong>GraphWar</strong> by Elbert Perez.
        </p>
        <p>
          Built with Phaser 4, TypeScript, and mathjs.
        </p>
        <button class="credits-close-btn" style="margin-top: 1rem;">Close</button>
      </div>
    `;

    document.body.appendChild(panel);
    this.creditsPanel = panel;

    const closeBtn = panel.querySelector('.credits-close-btn') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => {
      this.hideCredits();
    });

    // Close on backdrop click
    panel.addEventListener('click', (e) => {
      if (e.target === panel) this.hideCredits();
    });
  }

  private hideCredits(): void {
    if (this.creditsPanel) {
      this.creditsPanel.remove();
      this.creditsPanel = null;
    }
  }

  private tutorialPanel: HTMLDivElement | null = null;

  /**
   * Shows the How to Play tutorial modal.
   */
  private showTutorial(): void {
    if (this.tutorialPanel) return;

    const panel = document.createElement('div');
    panel.className = 'credits-panel';
    panel.innerHTML = `
      <div class="credits-card tutorial-card">
        <h2>How to Play Mathillery</h2>

        <div class="tutorial-section">
          <h3>Objective</h3>
          <p>Eliminate enemy soldiers by typing mathematical functions <code>f(x)</code>. Your projectile follows the curve of your equation on the Cartesian grid.</p>
        </div>

        <div class="tutorial-section">
          <h3>Firing & Direction</h3>
          <p>Team 1 (Blue) fires <strong>Right</strong>. Team 2 (Red) fires <strong>Left</strong>. The curve automatically passes through your active soldier's position.</p>
        </div>

        <div class="tutorial-section">
          <h3>Function Examples</h3>
          <ul class="tutorial-list">
            <li><code>x</code> or <code>0.5*x + 2</code> — Straight diagonal lines</li>
            <li><code>-0.1*x^2 + 4</code> — Parabolic arc lobbed over targets</li>
            <li><code>3*sin(x)</code> — Oscillating wave curve</li>
            <li><code>sqrt(abs(x))</code> — Curved root trajectory</li>
          </ul>
        </div>

        <div class="tutorial-section">
          <h3>Controls & Features</h3>
          <ul class="tutorial-list">
            <li><strong><code>↑</code> / <code>↓</code> Arrow Keys</strong> — Recall previous equations</li>
            <li><strong>History Panel</strong> — Inspect past turn logs and hit records</li>
            <li><strong>Traces Option</strong> — Choose visible or hidden trajectory line traces in setup</li>
          </ul>
        </div>

        <div class="tutorial-section">
          <h3>💡 Pro Tip: Trajectory Offset</h3>
          <p>Every function is automatically shifted so the curve <strong>passes through your soldier's position</strong>. This means using phase shifts like <code>sin(x-0.2)</code> won't just move the wave sideways — it will also shift it <strong>vertically</strong> to keep the curve anchored to your soldier.</p>
          <p style="color: var(--color-text-muted); font-size: 0.85rem;">Internally: <code>y = f(x) − f(0) + playerY</code>. If <code>f(0) ≠ 0</code>, the whole curve shifts up or down.</p>
        </div>

        <button class="credits-close-btn tutorial-close-btn">Got it!</button>
      </div>
    `;

    document.body.appendChild(panel);
    this.tutorialPanel = panel;

    const closeBtn = panel.querySelector('.tutorial-close-btn') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.hideTutorial());
    panel.addEventListener('click', (e) => {
      if (e.target === panel) this.hideTutorial();
    });
  }

  private hideTutorial(): void {
    if (this.tutorialPanel) {
      this.tutorialPanel.remove();
      this.tutorialPanel = null;
    }
  }

  /**
   * Shows the Watch Replay modal card.
   */
  private showReplayModal(callbacks: MenuCallbacks): void {
    if (this.replayModal) return;

    const recent = getRecentReplays();
    let recentHtml = '';

    if (recent.length > 0) {
      recentHtml = `
        <div class="setup-option-group" style="margin-top: 1rem;">
          <label class="setup-label">Recent Matches</label>
          <div style="display: flex; flex-direction: column; gap: 6px; max-height: 140px; overflow-y: auto;">
            ${recent.map((r, i) => {
              const dateStr = new Date(r.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return `
                <div style="display: flex; align-items: center; gap: 6px;">
                  <button class="menu-btn recent-replay-btn" data-index="${i}" style="flex: 1; text-align: left; padding: 8px 12px; font-size: 0.85rem;">
                    <strong>${r.team1Count}v${r.team2Count}</strong> — ${r.difficulty.toUpperCase()} (${r.turns.length} turns)
                    <span style="float: right; color: var(--text-muted); font-size: 0.75rem;">${dateStr}</span>
                  </button>
                  <button class="setup-modal-close btn-delete-replay" data-created="${r.createdAt}" title="Delete replay" style="padding: 6px 10px; font-size: 0.9rem;">✕</button>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    const modal = document.createElement('div');
    modal.className = 'credits-panel setup-modal-backdrop';
    modal.innerHTML = `
      <div class="credits-card setup-modal-card">
        <div class="setup-modal-header">
          <h2>Watch Replay</h2>
          <button class="setup-modal-close" id="btn-close-replay">✕</button>
        </div>

        <div class="setup-option-group">
          <label class="setup-label">Replay Code</label>
          <div style="display: flex; gap: 8px;">
            <input type="text" class="hud-function-input" id="replay-code-input" placeholder="Paste replay code here..." style="flex: 1;" autocomplete="off" spellcheck="false" />
            <button class="menu-btn menu-btn--primary" id="btn-load-code" style="width: auto; padding: 0 16px;">Load</button>
          </div>
        </div>

        ${recentHtml}
      </div>
    `;

    document.body.appendChild(modal);
    this.replayModal = modal;

    const closeBtn = modal.querySelector('#btn-close-replay') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.hideReplayModal());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.hideReplayModal();
    });

    const codeInput = modal.querySelector('#replay-code-input') as HTMLInputElement;
    const loadBtn = modal.querySelector('#btn-load-code') as HTMLButtonElement;

    const loadCode = (code: string) => {
      const data = importReplayCode(code);
      if (data) {
        this.hideReplayModal();
        callbacks.onWatchReplay(data);
      } else {
        this.showToast('Invalid or corrupted replay code');
      }
    };

    loadBtn.addEventListener('click', () => {
      loadCode(codeInput.value);
    });

    codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') loadCode(codeInput.value);
    });

    const recentBtns = modal.querySelectorAll('.recent-replay-btn');
    recentBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt((btn as HTMLElement).dataset.index || '0', 10);
        const data = recent[index];
        if (data) {
          this.hideReplayModal();
          callbacks.onWatchReplay(data);
        }
      });
    });

    const deleteBtns = modal.querySelectorAll('.btn-delete-replay');
    deleteBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const created = parseInt((btn as HTMLElement).dataset.created || '0', 10);
        deleteLocalReplay(created);
        
        // Remove the row from the DOM smoothly instead of reloading the whole modal
        const row = btn.parentElement;
        if (row) {
          row.remove();
        }
      });
    });
  }

  private hideReplayModal(): void {
    if (this.replayModal) {
      this.replayModal.remove();
      this.replayModal = null;
    }
  }

  /**
   * Shows a toast notification.
   */
  private showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  /**
   * Removes the menu from the DOM.
   */
  destroy(): void {
    this.hideCredits();
    this.hideTutorial();
    this.hideDifficultyPicker();
    this.hideReplayModal();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}
